import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sparkles, TrendingUp, Search, Users, Clock } from "lucide-react";
import { format } from "date-fns";

interface SearchRecord {
  id: string;
  user_id: string;
  query: string;
  created_at: string;
}

export default function HealthSearchAnalytics() {
  const { data: searches, isLoading } = useQuery({
    queryKey: ["health-faq-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("health_faq_searches" as any)
        .select("id, user_id, query, created_at")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data || []) as unknown as SearchRecord[];
    },
  });

  // Aggregate topics
  const topicCounts: Record<string, number> = {};
  const uniqueMembers = new Set<string>();
  searches?.forEach((s) => {
    const normalized = s.query.trim().toLowerCase();
    topicCounts[normalized] = (topicCounts[normalized] || 0) + 1;
    uniqueMembers.add(s.user_id);
  });

  const sortedTopics = Object.entries(topicCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 20);

  const totalSearches = searches?.length ?? 0;
  const uniqueTopics = Object.keys(topicCounts).length;
  const uniqueMemberCount = uniqueMembers.size;

  // Recent searches (last 25)
  const recentSearches = searches?.slice(0, 25) ?? [];

  return (
    <div className="min-h-screen bg-background">
      <ProviderSidebar />
      <div className="ml-64">
        <ProviderHeader />
        <main className="p-6">
          {/* Header */}
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground">
                Health Search Analytics
              </h1>
              <p className="text-sm text-muted-foreground">
                See what health topics your members are searching for
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card>
              <CardContent className="flex items-center gap-4 py-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <Search className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{totalSearches}</p>
                  <p className="text-xs text-muted-foreground">Total Searches</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 py-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/50">
                  <TrendingUp className="h-5 w-5 text-accent-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{uniqueTopics}</p>
                  <p className="text-xs text-muted-foreground">Unique Topics</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 py-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary">
                  <Users className="h-5 w-5 text-secondary-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{uniqueMemberCount}</p>
                  <p className="text-xs text-muted-foreground">Members Searching</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Top Topics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  Top Searched Topics
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                ) : sortedTopics.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No searches yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {sortedTopics.map(([topic, count], i) => (
                      <div
                        key={topic}
                        className="flex items-center justify-between rounded-lg border px-4 py-2.5"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-semibold text-muted-foreground w-5">
                            {i + 1}.
                          </span>
                          <span className="text-sm font-medium capitalize truncate">
                            {topic}
                          </span>
                        </div>
                        <Badge variant="secondary" className="shrink-0">
                          {count} {count === 1 ? "search" : "searches"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Searches */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-4 w-4 text-primary" />
                  Recent Searches
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                ) : recentSearches.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No searches yet
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Query</TableHead>
                        <TableHead className="w-[140px]">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentSearches.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium text-sm max-w-[300px] truncate">
                            {s.query}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {format(new Date(s.created_at), "MMM d, h:mm a")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
